import { useState } from "react";
import "./AddLinkFlow.scss";
import { useTranslation } from "react-i18next";

function AddLinkFlow({
  onSelectList,
  onSelectNote,
  onAddExternalLink,
  onClose,
  isUpdating,
  initialScreen = "type",
}) {
  const { t } = useTranslation();
  const [screen, setScreen] = useState(initialScreen); // 'type' | 'listChoice' | 'noteChoice' | 'externalLink' | 'email'
  const [linkName, setLinkName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [infoText, setInfoText] = useState("");
  const [infoType, setInfoType] = useState("");

  const goBack = () => {
    setScreen("type");
    setLinkName("");
    setLinkUrl("");
    setInfoText("");
    setInfoType("");
  };

  const handleSubmitLink = () => {
    onAddExternalLink(linkName, linkUrl, (text, type) => {
      setInfoText(text);
      setInfoType(type);
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isUpdating) {
      handleSubmitLink();
    }
  };

  // ── Screen: type selection ──────────────────────────────────
  if (screen === "type") {
    return (
      <div className="add-link-flow">
        <div className="link-type-grid">
          <button
            className="link-type-card"
            onClick={() => setScreen("listChoice")}
          >
            <div className="card-icon list-icon">
              <span className="material-symbols-outlined">list</span>
            </div>
            <div className="card-content">
              <h4>{t("tasks.links.types.list")}</h4>
              <p>{t("tasks.links.types.listDescription")}</p>
            </div>
          </button>

          <button
            className="link-type-card"
            onClick={() => setScreen("noteChoice")}
          >
            <div className="card-icon note-icon">
              <span className="material-symbols-outlined">note</span>
            </div>
            <div className="card-content">
              <h4>{t("tasks.links.types.note")}</h4>
              <p>{t("tasks.links.types.noteDescription")}</p>
            </div>
          </button>

          <button
            className="link-type-card"
            onClick={() => setScreen("externalLink")}
          >
            <div className="card-icon link-icon">
              <span className="material-symbols-outlined">link</span>
            </div>
            <div className="card-content">
              <h4>{t("tasks.links.types.externalLink")}</h4>
              <p>{t("tasks.links.types.externalLinkDescription")}</p>
            </div>
          </button>

          <button className="link-type-card" onClick={() => setScreen("email")}>
            <div className="card-icon email-icon">
              <span className="material-symbols-outlined">mail</span>
            </div>
            <div className="card-content">
              <h4>{t("tasks.links.types.email")}</h4>
              <p>{t("tasks.links.types.emailDescription")}</p>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // ── Screen: list choice ─────────────────────────────────────
  if (screen === "listChoice") {
    return (
      <div className="add-link-flow choice-screen">
        <button className="back-btn" onClick={goBack}>
          <span className="material-symbols-outlined">arrow_back</span>
          {t("common.back")}
        </button>
        <h3 className="flow-title">{t("tasks.links.linkList")}</h3>
        <div className="choice-buttons">
          <button
            className="choice-btn primary"
            onClick={() => onSelectList("create")}
          >
            <span className="material-symbols-outlined">add_circle</span>
            {t("tasks.links.createNewList")}
          </button>
          <button
            className="choice-btn secondary"
            onClick={() => onSelectList("link")}
          >
            <span className="material-symbols-outlined">link</span>
            {t("tasks.links.linkExistingList")}
          </button>
        </div>
      </div>
    );
  }

  // ── Screen: note choice ─────────────────────────────────────
  if (screen === "noteChoice") {
    return (
      <div className="add-link-flow choice-screen">
        <button className="back-btn" onClick={goBack}>
          <span className="material-symbols-outlined">arrow_back</span>
          {t("common.back")}
        </button>
        <h3 className="flow-title">{t("tasks.links.linkNote")}</h3>
        <div className="choice-buttons">
          <button
            className="choice-btn primary"
            onClick={() => onSelectNote("create")}
          >
            <span className="material-symbols-outlined">add_circle</span>
            {t("tasks.links.createNewNote")}
          </button>
          <button
            className="choice-btn secondary"
            onClick={() => onSelectNote("link")}
          >
            <span className="material-symbols-outlined">link</span>
            {t("tasks.links.linkExistingNote")}
          </button>
        </div>
      </div>
    );
  }

  // ── Shared form for link-type screens ───────────────────────
  const renderLinkForm = ({
    flowTitle,
    urlLabel,
    urlPlaceholder,
    urlInputType,
    screenClass,
  }) => (
    <div className={`add-link-flow ${screenClass}`}>
      <button className="back-btn" onClick={goBack} disabled={isUpdating}>
        <span className="material-symbols-outlined">arrow_back</span>
        {t("common.back")}
      </button>
      <h3 className="flow-title">{flowTitle}</h3>
      <div className="form-content">
        <div className="input-group">
          <label htmlFor="flow-link-name">
            {t("tasks.links.nameOptional")}
          </label>
          <input
            id="flow-link-name"
            type="text"
            placeholder={t("tasks.links.friendlyName")}
            value={linkName}
            onChange={(e) => {
              setLinkName(e.target.value);
              setInfoText("");
            }}
            onKeyPress={handleKeyPress}
            className="input-field"
          />
        </div>
        <div className="input-group">
          <label htmlFor="flow-link-url">{urlLabel}</label>
          <input
            id="flow-link-url"
            type={urlInputType}
            placeholder={urlPlaceholder}
            value={linkUrl}
            onChange={(e) => {
              setLinkUrl(e.target.value);
              setInfoText("");
            }}
            onKeyPress={handleKeyPress}
            className="input-field"
            autoFocus
          />
        </div>
        {infoText && (
          <div className={`info-message ${infoType}`}>{infoText}</div>
        )}
        <div className="form-actions">
          <button
            className="cancel-btn"
            onClick={onClose}
            disabled={isUpdating}
          >
            {t("common.cancel")}
          </button>
          <button
            className={`add-btn ${isUpdating ? "updating" : ""}`}
            onClick={handleSubmitLink}
            disabled={isUpdating}
          >
            <span className="material-symbols-outlined">add</span>
            {isUpdating ? t("common.adding") : t("common.add")}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Screen: external link form ──────────────────────────────
  if (screen === "externalLink") {
    return renderLinkForm({
      flowTitle: t("tasks.links.linkExternal"),
      urlLabel: "URL",
      urlPlaceholder: "https://example.com",
      urlInputType: "text",
      screenClass: "external-link-screen",
    });
  }

  // ── Screen: email form ──────────────────────────────────────
  if (screen === "email") {
    return renderLinkForm({
      flowTitle: t("tasks.links.linkEmail"),
      urlLabel: t("tasks.links.emailAddress"),
      urlPlaceholder: "user@example.com",
      urlInputType: "email",
      screenClass: "email-screen",
    });
  }

  return null;
}

export default AddLinkFlow;
